"use client"

// /about — client sections (V3-MASTER-SPEC §2.7; copy deck v2 §4).
// Register: entirely PAPER — this page is testimony, not instrumentation.
// Worldview essay links land when /essays ships (W3–4) — no dead links now.

import Image from "next/image"
import { CtaBlock } from "@/components/v3/site-chrome"
import { Eyebrow, FadeUp, ZH_SERIF, paper } from "@/components/v3/primitives"
import { about, identity } from "@/lib/proto/content"

// ── (1) 背景線 + (2) 定位段 + 專業照 ─────────────────────────────────────────

export function IntroSection() {
  return (
    <section aria-label="背景" className="relative mx-auto max-w-6xl px-6 pb-20 pt-28 md:px-10 md:pb-28 md:pt-36">
      <FadeUp>
        <Eyebrow zh="關於" en="About" />
        <div className="mt-8 flex flex-wrap items-baseline gap-x-5 gap-y-2">
          <h1
            className="text-4xl leading-[1.15] md:text-[3.6rem]"
            style={{ fontFamily: "var(--proto-fraunces)", fontWeight: 500, color: paper.ink }}
          >
            {identity.name}
          </h1>
          <span
            className="text-[13px] tracking-[0.14em]"
            style={{ fontFamily: "var(--proto-mono)", color: paper.inkFaint }}
          >
            {identity.role}
          </span>
        </div>
      </FadeUp>

      <div className="mt-14 grid grid-cols-1 gap-12 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] lg:gap-16">
        <div>
          {/* §4.1 背景線 — LOCKED（無年份版） */}
          <FadeUp delay={0.08}>
            <p
              lang="zh-Hant"
              className="max-w-4xl text-balance text-2xl leading-[1.7] md:text-[2.2rem] md:leading-[1.65]"
              style={{ fontFamily: ZH_SERIF, fontVariationSettings: "'wght' 520", color: paper.ink }}
            >
              {about.background.zh}
            </p>
            <p
              className="mt-6 max-w-3xl text-[13px] leading-relaxed"
              style={{ fontFamily: "var(--proto-hanken)", color: paper.inkFaint }}
            >
              {about.background.en}
            </p>
          </FadeUp>

          {/* §4.2 定位段 — /about 渲染版 A1 前導半句（裁定 D7）
              v1.1 (2026-07-11 Raychard 拍板 via full-adoption)：主詞回到人，
              機構變成他的作品；deck 定位段本體（about.positioning）不動 */}
          <FadeUp delay={0.12}>
            <p
              lang="zh-Hant"
              className="mt-16 max-w-3xl pl-6 text-lg leading-[1.9]"
              style={{
                borderLeft: `2px solid ${paper.accent}`,
                fontFamily: "var(--proto-hanken)",
                color: paper.inkSoft,
              }}
            >
              {about.positioningAboutPage.zh}
            </p>
            <p
              className="mt-4 max-w-3xl pl-6 text-[13px] leading-relaxed"
              style={{ fontFamily: "var(--proto-hanken)", color: paper.inkFaint }}
            >
              {about.positioningAboutPage.en}
            </p>
          </FadeUp>
        </div>

        {/* §2.7 專業照 — carried over from the current production site */}
        <FadeUp delay={0.15} className="lg:justify-self-end">
          <figure
            className="relative max-w-sm overflow-hidden rounded-2xl"
            style={{ border: `1px solid ${paper.hairline}` }}
          >
            <Image
              src="/images/raychard-website-photo.png"
              alt="Raychard Huang · Creative Intelligence Officer, RedPeak"
              width={848}
              height={1264}
              sizes="(min-width: 1024px) 24rem, 100vw"
              className="h-auto w-full"
              priority
            />
          </figure>
        </FadeUp>
      </div>
    </section>
  )
}

// ── (3) Worldview 三問收斂段 ─────────────────────────────────────────────────

export function WorldviewSection() {
  return (
    <section
      aria-label="三個長期問題"
      className="relative py-24 md:py-36"
      style={{
        background: paper.bgDeep,
        borderTop: `1px solid ${paper.hairlineSoft}`,
        borderBottom: `1px solid ${paper.hairlineSoft}`,
      }}
    >
      <div className="mx-auto max-w-6xl px-6 md:px-10">
        <FadeUp>
          <Eyebrow zh="工作之下" en="Beneath the work" />
        </FadeUp>
        <div className="mt-12 grid grid-cols-1 gap-12 lg:grid-cols-[minmax(0,0.7fr)_minmax(0,1.3fr)]">
          <FadeUp delay={0.05}>
            <p
              lang="zh-Hant"
              className="text-2xl leading-[1.6] md:text-[1.9rem]"
              style={{ fontFamily: ZH_SERIF, fontVariationSettings: "'wght' 560", color: paper.ink }}
            >
              {about.worldview.intro.zh}
            </p>
            <p
              className="mt-4 text-[13px] leading-relaxed"
              style={{ fontFamily: "var(--proto-hanken)", color: paper.inkFaint }}
            >
              {about.worldview.intro.en}
            </p>
          </FadeUp>
          <div className="space-y-10">
            {about.worldview.questions.map((q, i) => (
              <FadeUp key={q.name.en} delay={i * 0.06}>
                <div className="grid grid-cols-[5rem_minmax(0,1fr)] gap-6">
                  <span
                    lang="zh-Hant"
                    className="text-2xl"
                    style={{
                      fontFamily: ZH_SERIF,
                      fontVariationSettings: "'wght' 640",
                      color: paper.accent,
                    }}
                  >
                    {q.name.zh}
                  </span>
                  <p
                    lang="zh-Hant"
                    className="text-[16px] leading-[1.9]"
                    style={{ fontFamily: "var(--proto-hanken)", color: paper.inkSoft }}
                  >
                    {q.line.zh}
                  </p>
                </div>
              </FadeUp>
            ))}
            <FadeUp delay={0.2}>
              <p
                lang="zh-Hant"
                className="max-w-2xl pt-8 text-[15px] leading-[1.95]"
                style={{
                  fontFamily: "var(--proto-hanken)",
                  borderTop: `1px solid ${paper.hairline}`,
                  color: paper.inkSoft,
                }}
              >
                {about.worldview.outro.zh}
              </p>
            </FadeUp>
          </div>
        </div>
      </div>
    </section>
  )
}

export function AboutCta() {
  return <CtaBlock />
}
